import { useEffect, useState, useCallback } from 'react';
import { useStateWithCallbackLazy } from 'use-state-with-callback';
import { useElementSize } from '../../CustomHooks';
import StandardCard from '../StandardCard';
import StandardsByUnit from '../StandardsByUnit';
import {
  ElementaryLessonsWithStandards,
  getCurriculumNames,
  getUniqueMetStandards,
  getCompleteAndIncompleteByUnit,
} from '../../CurriculumElementaryDashboard/elementaryCompletionHelpers';
import { getCoreStandardsObject } from '../../core/context/contextHelpers';
import styles from './StandardsDashboardElementary.module.scss';
import { ClassroomType } from '../../../peekapak-types/DataProtocolTypes';
import { CoreStandard } from '../../../peekapak-types/LessonPlanTypes';

interface Props {
  handleResize: () => void;
  classroom: ClassroomType;
  allStandards: string[];
  completedLessons: ElementaryLessonsWithStandards;
  incompleteLessons: ElementaryLessonsWithStandards;
}

const StandardsDashboardElementary = ({
  handleResize,
  classroom,
  allStandards,
  completedLessons,
  incompleteLessons,
}: Props): JSX.Element => {
  const [isStandardsToggle, setIsStandardsToggle] = useStateWithCallbackLazy(true);
  const [standardsLibrary, setStandardsLibrary] = useState<{
    [key: string]: CoreStandard;
  }>();
  const [_, setExpandTrigger] = useStateWithCallbackLazy(false);
  const [contentHeight, setContentHeight] = useState('0px');
  const [standardsRef, { height }] = useElementSize();
  const handleHeightChange = () => {
    setExpandTrigger(
      (prevState) => !prevState,
      () => handleResize(),
    );
  };

  const handleStandardsToggle = (isStandards: boolean) => {
    setIsStandardsToggle(isStandards, () => handleResize());
  };

  const uniqueStandards = [...new Set(allStandards)];

  const completedStandards = getUniqueMetStandards(completedLessons);

  const incompleteStandards = uniqueStandards.filter((item) => !completedStandards.includes(item));

  const standardsByUnit = getCompleteAndIncompleteByUnit(completedLessons, incompleteLessons);

  const coreUnitsOnly = standardsByUnit.filter((unit) => getCurriculumNames().includes(unit.id));

  const getStandardDescriptions = useCallback(async () => {
    const coreStandards = await getCoreStandardsObject();
    setStandardsLibrary(coreStandards);
  }, []);

  useEffect(() => {
    getStandardDescriptions();
  }, []);

  useEffect(() => {
    standardsRef.current && setContentHeight(`${standardsRef.current.scrollHeight}px`);
    handleResize();
  }, [height, contentHeight, classroom, standardsLibrary]);

  return (
    <div className={styles.container}>
      <div className={styles.toggleBar}>
        <div
          className={styles.toggleOption}
          style={{
            borderBottom: isStandardsToggle ? '3px solid #00afd8' : '',
            color: isStandardsToggle ? '#012a73' : 'inherit',
          }}
          onClick={() => handleStandardsToggle(true)}
        >
          By Standard
        </div>
        <div
          className={styles.toggleOption}
          style={{
            borderBottom: !isStandardsToggle ? '3px solid #00afd8' : '',
            color: !isStandardsToggle ? '#012a73' : 'inherit',
          }}
          onClick={() => handleStandardsToggle(false)}
        >
          By Unit
        </div>
      </div>
      {isStandardsToggle ? (
        <div className={styles.byStandardContainer} ref={standardsRef}>
          <div className={styles.standardsList}>
            <h3 className={styles.title}>Completed Standards</h3>
            {standardsLibrary &&
              completedStandards.map((standard) => {
                return (
                  <StandardCard
                    key={standard}
                    name={standardsLibrary[standard].label}
                    description={standardsLibrary[standard].text}
                    lessons={completedLessons.filter((item) => item.standards.includes(standard))}
                    classroom={classroom}
                    passExpandToParent={handleHeightChange}
                    isComplete
                  />
                );
              })}
          </div>
          <div className={styles.standardsList}>
            <h3 className={styles.title}>Incomplete Standards</h3>
            {standardsLibrary &&
              incompleteStandards.map((standard) => {
                return (
                  <StandardCard
                    key={standard}
                    name={standardsLibrary[standard].label}
                    description={standardsLibrary[standard].text}
                    lessons={incompleteLessons.filter((item) => item.standards.includes(standard))}
                    classroom={classroom}
                    passExpandToParent={handleHeightChange}
                  />
                );
              })}
          </div>
        </div>
      ) : (
        standardsLibrary && (
          <StandardsByUnit
            colA='Unit Name'
            colB='Standards Completion'
            colC='Complete'
            colD='Incomplete'
            data={coreUnitsOnly}
            barColor='#ff6319'
            isShouldAnimate
            standardsLibrary={standardsLibrary}
          />
        )
      )}
    </div>
  );
};

export default StandardsDashboardElementary;
